<template>
  <div class="code-input" role="group">
    <input
      v-for="(digit, index) in digits"
      :key="index"
      ref="cells"
      class="code-input__cell"
      type="text"
      inputmode="numeric"
      pattern="[0-9]*"
      autocomplete="one-time-code"
      maxlength="1"
      :aria-label="`Digit ${index + 1}`"
      :value="digit"
      @input="onInput(index, $event)"
      @keydown="onKeydown(index, $event)"
      @paste.prevent="onPaste($event, index)"
      @focus="$event.target.select()"
      @change.stop
      @blur="onBlur" />
  </div>
</template>

<script>
import Emitter from 'element-ui/src/mixins/emitter';

export default {
  name: 'CodeInput',
  mixins: [Emitter],

  props: {
    value: {
      type: String,
      default: '',
    },
    length: {
      type: Number,
      default: 6,
    },
  },

  computed: {
    digits() {
      const value = String(this.value || '')
        .replace(/\D/g, '')
        .slice(0, this.length);

      return Array.from({ length: this.length }, (_, index) => value[index] || '');
    },
  },

  methods: {
    emitValue(digits) {
      this.$emit('input', digits.join(''));
    },

    focusCell(index) {
      const cells = this.$refs.cells || [];
      const cell = cells[Math.max(0, Math.min(index, this.length - 1))];

      if (cell) {
        cell.focus();
        cell.select();
      }
    },

    onInput(index, event) {
      const raw = String(event.target.value || '').replace(/\D/g, '');

      if (raw.length > 1) {
        this.fillFromString(raw, index);
        return;
      }

      const nextDigits = [...this.digits];
      nextDigits[index] = raw.slice(-1);
      this.emitValue(nextDigits);

      if (raw) {
        this.$nextTick(() => this.focusCell(index + 1));
      }
    },

    onKeydown(index, event) {
      if (/^[0-9]$/.test(event.key)) {
        event.preventDefault();

        const nextDigits = [...this.digits];
        nextDigits[index] = event.key;
        this.emitValue(nextDigits);
        this.$nextTick(() => this.focusCell(index + 1));
        return;
      }

      if (event.key === 'Backspace') {
        event.preventDefault();

        const nextDigits = [...this.digits];

        if (nextDigits[index]) {
          nextDigits[index] = '';
          this.emitValue(nextDigits);
          return;
        }

        if (index > 0) {
          nextDigits[index - 1] = '';
          this.emitValue(nextDigits);
          this.$nextTick(() => this.focusCell(index - 1));
        }

        return;
      }

      if (event.key === 'Delete') {
        event.preventDefault();
        const nextDigits = [...this.digits];
        nextDigits[index] = '';
        this.emitValue(nextDigits);
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.focusCell(index - 1);
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.focusCell(index + 1);
      }
    },

    onPaste(event, index) {
      const pasted = (event.clipboardData || window.clipboardData).getData('text');
      this.fillFromString(pasted, index);
    },

    onBlur() {
      this.$nextTick(() => {
        const cells = this.$refs.cells || [];
        const focused = cells.some(cell => cell === document.activeElement);

        if (!focused) {
          this.$emit('blur', this.value);
          this.dispatch('ElFormItem', 'el.form.blur', [this.value]);
        }
      });
    },

    fillFromString(value, startIndex = 0) {
      const chars = String(value || '')
        .replace(/\D/g, '')
        .slice(0, this.length - startIndex)
        .split('');

      if (!chars.length) {
        return;
      }

      const nextDigits = [...this.digits];
      chars.forEach((char, offset) => {
        nextDigits[startIndex + offset] = char;
      });

      this.emitValue(nextDigits);
      this.$nextTick(() => this.focusCell(startIndex + chars.length));
    },
  },
};
</script>

<style lang="scss">
.code-input {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 9px;
  width: 100%;

  &__cell {
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 0;
    width: 68px;
    height: 58px;
    flex: 1 1 68px;
    max-width: 68px;
    min-width: 0;
    background: #ffffff;
    border: 1px solid #bfc0c5;
    border-radius: 10px;
    color: #161616;
    font-family: 'Roboto', sans-serif;
    font-weight: 500;
    font-size: 24px;
    line-height: 58px;
    text-align: center;
    outline: none;
    caret-color: #334150;
    appearance: none;
    -webkit-appearance: none;

    &:focus {
      border: 2px solid #bfc0c5;
      box-shadow: 0px 0px 0px 3px #d43b4a29;
    }
  }
}

html[data-color-scheme='dark'] {
  .code-input__cell {
    background: #353538;
    border-color: transparent;
    color: #ffffff;
    caret-color: #ffffff;
    border-color: #727276;

    &:focus {
      border: 2px solid #d43b4a;
      box-shadow: 0px 0px 0px 3px #d43b4a29;
    }
  }
}
</style>
